import has from 'lodash/has';
import type { Asserts } from 'yup';

import type {
  clientSchema,
  persistedClientSchema,
} from '@/features/Clients/Schemas/Base';
import { getDefaultClientResource } from '@/features/Clients/utils';
import { EntityResourceContainer } from '@/features/EntityManager/types';
import { clientSchemaPWApi } from '@/features/Clients/Schemas/Base';

export enum ROLE {
  VERSICHERUNGSNEHMER = 'versicherungsnehmer',
  ZAHLPERSON = 'zahlPerson',
  MITVERSICHERTER_PARTNER = 'mitversicherterPartner',
  VERSICHERTE_PERSON = 'versichertePerson',
}

export enum OPTIONAL_ROLE {
  // UNF
  VERSICHERTE_PERSON_2 = 'versichertePerson2',
  VERSICHERTE_PERSON_3 = 'versichertePerson3',
  VERSICHERTE_PERSON_4 = 'versichertePerson4',
  VERSICHERTE_PERSON_5 = 'versichertePerson5',
  VERSICHERTE_PERSON_6 = 'versichertePerson6',
  // KFZ
  ABWEICHENDER_HALTER = 'abweichenderHalter',
  FAHRER_1 = 'fahrer1',
  FAHRER_2 = 'fahrer2',
  FAHRER_3 = 'fahrer3',
  FAHRER_4 = 'fahrer4',
  FAHRER_5 = 'fahrer5',
}

export type Role = ROLE | OPTIONAL_ROLE;

export const isRole = (str: unknown): str is Role => {
  const validRoles = [...Object.values(ROLE), ...Object.values(OPTIONAL_ROLE)];

  return validRoles.includes(str as Role);
};

export const RoleLabel: {
  [key in Role]: string;
} = {
  [ROLE.VERSICHERUNGSNEHMER]: 'Versicherungsnehmer',
  [ROLE.ZAHLPERSON]: 'Zahlperson',
  [ROLE.MITVERSICHERTER_PARTNER]: 'Mitversicherter Partner',
  [ROLE.VERSICHERTE_PERSON]: 'Versicherte Person 1',
  [OPTIONAL_ROLE.VERSICHERTE_PERSON_2]: 'Versicherte Person 2',
  [OPTIONAL_ROLE.VERSICHERTE_PERSON_3]: 'Versicherte Person 3',
  [OPTIONAL_ROLE.VERSICHERTE_PERSON_4]: 'Versicherte Person 4',
  [OPTIONAL_ROLE.VERSICHERTE_PERSON_5]: 'Versicherte Person 5',
  [OPTIONAL_ROLE.VERSICHERTE_PERSON_6]: 'Versicherte Person 6',
  [OPTIONAL_ROLE.ABWEICHENDER_HALTER]: 'Abweichender Halter',
  [OPTIONAL_ROLE.FAHRER_1]: 'Fahrer 1',
  [OPTIONAL_ROLE.FAHRER_2]: 'Fahrer 2',
  [OPTIONAL_ROLE.FAHRER_3]: 'Fahrer 3',
  [OPTIONAL_ROLE.FAHRER_4]: 'Fahrer 4',
  [OPTIONAL_ROLE.FAHRER_5]: 'Fahrer 5',
};

export type ClientResource = Asserts<typeof clientSchema>;
export type ClientResourcePWApi = Asserts<typeof clientSchemaPWApi>;
export type ClientResourcePersisted = Asserts<typeof persistedClientSchema>;

type NestedAddressKeys = `adresse.${keyof ClientResource['adresse']}`;
type NestedBankingKeys =
  `bankverbindung.${keyof ClientResource['bankverbindung']}`;
export type ClientResourceKeys = keyof ClientResource;

export type ClientResourceKeysWithNested =
  | ClientResourceKeys
  | keyof ClientResource['adresse']
  | keyof ClientResource['bankverbindung'];

export type ClientResourceKeysNestedAsPath =
  | ClientResourceKeys
  | NestedAddressKeys
  | NestedBankingKeys;

export const isClientResourceKey = (
  key: string,
): key is ClientResourceKeysWithNested => has(getDefaultClientResource(), key);

export interface ClientResourceContainer<T = ClientResource>
  extends EntityResourceContainer<T> {
  roles: Role[];
}

export interface LegacyClientResourceContainerQuoteStructure {
  _refId: number;
  type: 'Client';
  data: ClientResource;
  isLoading: boolean;
  dirty: boolean;
  roles: Role[];
}

export const ANREDE_FIRMA = 3;
